import dayjs, { Dayjs } from "dayjs";
import { ExpressContact } from "../../types/express-master.type";
import {
  ExpressCreditBill,
  ExpressDocumentDetail,
  ExpressPriceType,
} from "../../types/express-document.type";
import { DecimalValue } from "../../types/credit-debit-note.type";
import { roundHalfUp } from "../../utils/money";
import { VAT_RATE_PERCENT } from "../../utils/vat";
import { CustomerOption } from "../SpecialOrder/CustomerPicker";
import { DEFAULT_PRICE_TYPE, ExpressDocTypeConfig } from "./constants";
import {
  ExpressPaymentTotals,
  ExpressTotals,
  computeExpressTotals,
  computePaymentTotals,
  lineAmount,
  sumAmounts,
} from "./totals";

/**
 * The entry form's state, shared by the four types, and the pure conversions around it: a new
 * draft, a saved document read back into a draft, and the totals the draft previews. Figures
 * stay text while they are keyed, so a half-typed "12." is not rounded away under the cursor.
 */

/** Who the document is with, as the left panel shows it and the tax report prints it. */
export interface PartyDraft {
  /** The Express contact picked (R5 / P4 / O1); null on E1, whose party is a GDP customer. */
  contactUuid: string | null;
  code: string;
  title: string | null;
  name: string;
  taxId: string | null;
  taxBranchNo: number | null;
  address: string | null;
  phone: string | null;
}

/** An account the line or payment line books to; the name is shown, the code is sent. */
export interface AccountRef {
  code: string;
  name: string;
}

export interface LineDraft {
  key: string;
  account: AccountRef | null;
  description: string;
  warehouseCode: string;
  qty: string;
  unitPrice: string;
  /** O1 only: the amount typed in. */
  amount: string;
}

export interface BillLineDraft {
  billNo: string;
  billDate: string;
  salespersonCode: string | null;
  billAmount: number;
  /** What was still owed when the bill was picked (or the receipt saved). */
  outstandingAmount: number;
  payAmount: string;
  dueDate: string | null;
  reference: string | null;
}

export interface PaymentLineDraft {
  key: string;
  account: AccountRef | null;
  chequeDate: Dayjs | null;
  amount: string;
}

export interface DocumentDraft {
  /** The branch a cross-branch role keys for; unused by a branch user and on a saved document. */
  branchUuid: string;
  party: PartyDraft | null;
  /** The GDP customer an E1 picks; a saved receipt keeps its customer, so it has none. */
  customer: CustomerOption | null;
  docDate: Dayjs | null;
  remark: string;
  reference: string;
  salespersonCode: string;
  paymentDate: Dayjs | null;
  supplierBillNo: string;
  supplierBillDate: Dayjs | null;
  /** ยื่นงวด, held as the first day of its month. */
  taxPeriod: Dayjs | null;
  creditDays: string;
  priceType: ExpressPriceType;
  discountAmount: string;
  cashAmount: string;
  whtAmount: string;
  lines: LineDraft[];
  billLines: BillLineDraft[];
  paymentLines: PaymentLineDraft[];
}

let rowKeySequence = 0;
const nextRowKey = () => {
  rowKeySequence += 1;
  return `row-${rowKeySequence}`;
};

export const newLine = (): LineDraft => ({
  key: nextRowKey(),
  account: null,
  description: "",
  warehouseCode: "",
  qty: "1",
  unitPrice: "",
  amount: "",
});

export const newPaymentLine = (): PaymentLineDraft => ({
  key: nextRowKey(),
  account: null,
  chequeDate: null,
  amount: "",
});

export const createInitialDraft = (config: ExpressDocTypeConfig): DocumentDraft => ({
  branchUuid: "",
  party: null,
  customer: null,
  docDate: dayjs(),
  remark: "",
  reference: "",
  salespersonCode: "",
  paymentDate: config.lineMode === "BILLS" ? dayjs() : null,
  supplierBillNo: "",
  supplierBillDate: null,
  taxPeriod: null,
  creditDays: "",
  priceType: DEFAULT_PRICE_TYPE,
  discountAmount: "",
  cashAmount: "",
  whtAmount: "",
  lines: config.lineMode === "BILLS" ? [] : [newLine()],
  billLines: [],
  paymentLines: [],
});

/** Digits, one decimal point with at most `decimals` places, and a leading minus when allowed. */
export const sanitizeDecimal = (text: string, decimals: number, allowNegative = false): string => {
  const isNegative = allowNegative && text.trim().startsWith("-");
  const [whole, ...fractionParts] = text.replace(/[^\d.]/g, "").split(".");
  const unsigned =
    fractionParts.length === 0 ? whole : `${whole}.${fractionParts.join("").slice(0, decimals)}`;
  return isNegative ? `-${unsigned}` : unsigned;
};

/** Digits only — day counts. */
export const sanitizeInteger = (text: string): string => text.replace(/\D/g, "");

/** A keyed figure as a number; blank, "-" and "." count as nothing keyed. */
export const toNumber = (text: string): number => {
  const value = Number(text.trim());
  return Number.isFinite(value) ? value : 0;
};

/** A stored figure as editable text: "1756.40" → "1756.4"; nothing stored → blank. */
const toText = (value: DecimalValue | null | undefined): string =>
  value === null || value === undefined || value === "" ? "" : String(Number(value));

const toDay = (date: string | null): Dayjs | null => (date ? dayjs(date) : null);

export const partyFromContact = (contact: ExpressContact): PartyDraft => ({
  contactUuid: contact.uuid,
  code: contact.code,
  title: contact.title,
  name: contact.name,
  taxId: contact.tax_id,
  taxBranchNo: contact.tax_branch_no,
  address: contact.address,
  phone: contact.phone,
});

/** A GDP customer as an E1's party; the server snapshots the rest of its details when it saves. */
export const partyFromCustomer = (customer: CustomerOption): PartyDraft => ({
  contactUuid: null,
  code: customer.customer_code,
  title: null,
  name: customer.name,
  taxId: null,
  taxBranchNo: null,
  address: null,
  phone: null,
});

export const billLineFromCreditBill = (bill: ExpressCreditBill): BillLineDraft => ({
  billNo: bill.bill_no,
  billDate: bill.bill_date,
  salespersonCode: bill.salesperson_code,
  billAmount: Number(bill.bill_amount),
  outstandingAmount: Number(bill.outstanding_amount),
  payAmount: toText(bill.outstanding_amount),
  dueDate: bill.due_date,
  reference: bill.reference,
});

const partyFromDocument = (document: ExpressDocumentDetail): PartyDraft | null =>
  document.party_code
    ? {
        contactUuid: document.express_contact_uuid,
        code: document.party_code,
        title: document.party_title,
        name: document.party_name ?? "",
        taxId: document.party_tax_id,
        taxBranchNo: document.party_tax_branch_no,
        address: document.party_address,
        phone: document.party_phone,
      }
    : null;

/** A saved document read back into the form, to be viewed or edited. */
export const draftFromDocument = (document: ExpressDocumentDetail): DocumentDraft => ({
  branchUuid: "",
  party: partyFromDocument(document),
  customer: null,
  docDate: toDay(document.doc_date),
  remark: document.remark ?? "",
  reference: document.reference ?? "",
  salespersonCode: document.salesperson_code ?? "",
  paymentDate: toDay(document.payment_date),
  supplierBillNo: document.supplier_bill_no ?? "",
  supplierBillDate: toDay(document.supplier_bill_date),
  taxPeriod: document.tax_period ? dayjs(`${document.tax_period}-01`) : null,
  creditDays: document.credit_days === null ? "" : String(document.credit_days),
  priceType: document.price_type ?? DEFAULT_PRICE_TYPE,
  discountAmount: toText(document.discount_amount),
  cashAmount: toText(document.cash_amount),
  whtAmount: toText(document.wht_amount),
  lines: document.lines.map((line) => ({
    key: nextRowKey(),
    account: { code: line.account_code, name: line.account_name },
    description: line.description,
    warehouseCode: line.warehouse_code ?? "",
    qty: toText(line.qty),
    unitPrice: toText(line.unit_price),
    amount: toText(line.amount),
  })),
  billLines: document.bill_lines.map((line) => ({
    billNo: line.bill_no,
    billDate: line.bill_date,
    salespersonCode: line.salesperson_code,
    billAmount: Number(line.bill_amount),
    outstandingAmount: Number(line.outstanding_amount),
    payAmount: toText(line.pay_amount),
    dueDate: line.due_date,
    reference: line.reference,
  })),
  paymentLines: document.payment_lines.map((line) => ({
    key: nextRowKey(),
    account: { code: line.account_code, name: line.account_name },
    chequeDate: toDay(line.cheque_date),
    amount: toText(line.amount),
  })),
});

/** A line's amount as the server works it out: qty × unit price, or the O1 amount as typed. */
export const draftLineAmount = (line: LineDraft, config: ExpressDocTypeConfig): number =>
  config.lineMode === "QTY_PRICE"
    ? lineAmount(toNumber(line.qty), toNumber(line.unitPrice))
    : roundHalfUp(toNumber(line.amount));

/** ยอดชำระตามใบกำกับ — what the receipt pays across its bills. */
export const draftBillPayTotal = (draft: DocumentDraft): number =>
  sumAmounts(draft.billLines.map((line) => toNumber(line.payAmount)));

export const draftTotals = (
  draft: DocumentDraft,
  config: ExpressDocTypeConfig,
  vatRate: number,
): ExpressTotals =>
  computeExpressTotals(
    draft.lines.map((line) => draftLineAmount(line, config)),
    config.hasDiscount ? toNumber(draft.discountAmount) : 0,
    draft.priceType,
    vatRate,
  );

export const draftPaymentTotals = (draft: DocumentDraft): ExpressPaymentTotals =>
  computePaymentTotals(
    toNumber(draft.cashAmount),
    toNumber(draft.whtAmount),
    draft.paymentLines.map((line) => toNumber(line.amount)),
  );

/** The rate a document is priced at: its own once saved, else the legal rate. */
export const documentVatRate = (document: ExpressDocumentDetail | null): number =>
  document?.vat_rate === null || document?.vat_rate === undefined
    ? VAT_RATE_PERCENT
    : Number(document.vat_rate);

/** ครบกำหนด preview: the document date plus the credit days; the server stores its own. */
export const draftDueDate = (draft: DocumentDraft): Dayjs | null =>
  draft.docDate?.isValid() ? draft.docDate.add(toNumber(draft.creditDays), "day") : null;

/** The totals a saved document stored, shown instead of a preview once it is only viewed. */
export const storedTotals = (document: ExpressDocumentDetail): ExpressTotals => ({
  subtotal: Number(document.subtotal),
  discountAmount: Number(document.discount_amount),
  baseAmount: Number(document.base_amount),
  vatAmount: Number(document.vat_amount),
  netAmount: Number(document.net_amount),
});

export const storedPaymentTotals = (document: ExpressDocumentDetail): ExpressPaymentTotals => ({
  otherPaymentAmount: Number(document.other_payment_amount ?? 0),
  receivedAmount: Number(document.received_amount ?? 0),
});
